import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  AlgorithmProvider,
  AlgorithmInfo,
  ParameterMetadata,
  DEFAULT_ALGORITHM_KEY
} from '../../utils/algorithmProvider';
import { API_BASE_URL } from '../../services/api';

const Settings: React.FC = () => {
  const navigate = useNavigate();

  // アルゴリズム選択の状態
  const [selectedAlgorithm, setSelectedAlgorithm] = useState<string>(DEFAULT_ALGORITHM_KEY);

  // アルゴリズム情報（プロバイダから取得）
  const [algorithms, setAlgorithms] = useState<AlgorithmInfo[]>([]);

  // 動的パラメータ管理
  const [algorithmParams, setAlgorithmParams] = useState<Record<string, Record<string, any>>>({});

  const [analysisParams, setAnalysisParams] = useState({
    analysis_period_days: 70  // デフォルトは70営業日（SimpleTrendの55営業日要求を満たす）
  });

  // 現在のパラメータに基づく必要日数（バックエンドから取得）
  const [calculatedRequiredDays, setCalculatedRequiredDays] = useState<number | null>(null);

  // パラメータ変更時に必要日数を再計算（SSOT原則: バックエンドに問い合わせ）
  useEffect(() => {
    const calculateRequiredDays = async () => {
      if (!selectedAlgorithm || !algorithmParams[selectedAlgorithm]) return;

      try {
        const response = await fetch(`${API_BASE_URL}/algorithms/calculate-required-days`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            algorithm_key: selectedAlgorithm,
            params: algorithmParams[selectedAlgorithm]
          })
        });

        if (response.ok) {
          const data = await response.json();
          setCalculatedRequiredDays(data.required_days);
        }
      } catch (error) {
        console.error('必要日数の計算エラー:', error);
      }
    };

    calculateRequiredDays();
  }, [selectedAlgorithm, algorithmParams]);

  // 初期化 - バックエンドからアルゴリズム取得とデフォルトパラメータ設定
  useEffect(() => {
    const initializeAlgorithms = async () => {
      await AlgorithmProvider.fetchAlgorithmsFromBackend();
      const allAlgorithms = AlgorithmProvider.getAllAlgorithms();
      setAlgorithms(allAlgorithms);

      // 最初のアルゴリズムをデフォルト選択
      if (allAlgorithms.length > 0 && !selectedAlgorithm) {
        setSelectedAlgorithm(allAlgorithms[0].key);
      }

      const initialParams: Record<string, Record<string, any>> = {};
      allAlgorithms.forEach((algo) => {
        initialParams[algo.key] = { ...algo.default_params };
      });
      setAlgorithmParams(initialParams);
    };

    initializeAlgorithms();
  }, []);

  // 初回ロード時に保存された設定を読み込み
  useEffect(() => {
    const loadSavedSettings = () => {
      try {
        const savedSettings = localStorage.getItem('algorithmSettings');
        if (savedSettings) {
          const settings = JSON.parse(savedSettings);
          setSelectedAlgorithm(settings.algorithm || DEFAULT_ALGORITHM_KEY);

          // 動的パラメータの復元
          if (settings.algorithmParams) {
            setAlgorithmParams(prev => ({ ...prev, ...settings.algorithmParams }));
          }

          if (settings.analysisParams) {
            setAnalysisParams(settings.analysisParams);
          }
        } else {
          // 初回起動時: デフォルトの分析期間を設定
          // 注: 必要日数はパラメータに依存するため、デフォルトは70日（余裕を持たせた値）
          setAnalysisParams({
            analysis_period_days: 70
          });

          console.log(`初回起動: 分析期間を70日（デフォルト）に設定`);
        }
      } catch (error) {
        console.error('保存された設定の読み込みエラー:', error);
      }
    };

    // アルゴリズム情報が読み込まれた後に設定を復元
    if (algorithms.length > 0) {
      loadSavedSettings();
    }
  }, [algorithms]);

  const handleBack = () => {
    navigate('/');
  };

  // パラメータ更新ハンドラ
  const updateParameter = (algorithmKey: string, paramKey: string, value: any) => {
    setAlgorithmParams(prev => ({
      ...prev,
      [algorithmKey]: {
        ...prev[algorithmKey],
        [paramKey]: value
      }
    }));
  };

  const handleSave = () => {
    // 設定をそのまま保存（バリデーションはUI上で行われている）
    const settingsData = {
      algorithm: selectedAlgorithm,
      algorithmParams,
      analysisParams
    };

    try {
      // 設定をlocalStorageに保存
      localStorage.setItem('algorithmSettings', JSON.stringify(settingsData));
      console.log('設定保存:', settingsData);
      alert(`設定を保存しました（分析期間: ${analysisParams.analysis_period_days}日）`);
    } catch (error) {
      console.error('設定保存エラー:', error);
      alert('設定の保存に失敗しました');
    }
  };

  const handleReset = () => {
    // デフォルト値に戻す
    const resetParams: Record<string, Record<string, any>> = {};
    algorithms.forEach(algo => {
      resetParams[algo.key] = { ...algo.default_params };
    });

    setAlgorithmParams(resetParams);

    // 分析期間もデフォルト値に戻す
    setAnalysisParams({ analysis_period_days: 70 });

    alert('デフォルト値にリセットしました');
  };

  // 選択されたアルゴリズムの情報を取得
  const getSelectedAlgorithmInfo = (): AlgorithmInfo | undefined => {
    return algorithms.find(algo => algo.key === selectedAlgorithm);
  };

  // パラメータの入力コンポーネントを生成
  const renderParameterInput = (param: ParameterMetadata, currentValue: any) => {
    const inputId = `param-${param.key}`;
    const selectedAlgoInfo = getSelectedAlgorithmInfo();

    if (param.type === 'boolean') {
      return (
        <select
          id={inputId}
          value={currentValue ? 'true' : 'false'}
          onChange={(e) => updateParameter(selectedAlgorithm, param.key, e.target.value === 'true')}
        >
          <option value="true">有効</option>
          <option value="false">無効</option>
        </select>
      );
    }
    
    const defaultValue = selectedAlgoInfo?.default_params[param.key] ?? 0;

    return (
      <input
        id={inputId}
        type="number"
        min={param.min}
        max={param.max}
        step={param.step || 0.1}
        value={currentValue !== undefined && currentValue !== null ? currentValue : defaultValue}
        onChange={(e) => {
          const value = e.target.value;
          // 空文字列の場合はそのまま更新（削除中の状態を許可）
          if (value === '') {
            updateParameter(selectedAlgorithm, param.key, '');
          } else {
            const newValue = parseFloat(value);
            // 有効な数値の場合のみ更新
            if (!isNaN(newValue)) {
              updateParameter(selectedAlgorithm, param.key, newValue);
            }
          }
        }}
        onBlur={(e) => {
          // フォーカスが外れた時、空文字列の場合はデフォルト値に戻す
          if (e.target.value === '') {
            updateParameter(selectedAlgorithm, param.key, defaultValue);
          }
        }}
      />
    );
  };

  // アルゴリズムデータが読み込まれるまでローディング表示
  if (algorithms.length === 0) {
    return (
      <div className="settings">
        <div className="settings-header">
          <button onClick={handleBack} className="back-button">
            ← ダッシュボードに戻る
          </button>
          <h2>アルゴリズム設定</h2>
        </div>
        <div className="settings-content">
          <p>アルゴリズム情報を読み込んでいます...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="settings">
      <div className="settings-header">
        <button onClick={handleBack} className="back-button">
          ← ダッシュボードに戻る
        </button>
        <h2>アルゴリズム設定</h2>
      </div>

      <div className="settings-content">
        
        {/* アルゴリズム選択 */}
        <section className="algorithm-selection">
          <h3>検出アルゴリズム</h3>
          <div className="algorithm-options">
            {algorithms.map((algo) => (
              <label key={algo.key} className="algorithm-option">
                <input
                  type="radio"
                  name="algorithm"
                  value={algo.key}
                  checked={selectedAlgorithm === algo.key}
                  onChange={(e) => setSelectedAlgorithm(e.target.value)}
                />
                <div className="algorithm-info">
                  <span className="algorithm-name">{algo.name}</span>
                  <span className="algorithm-description">{algo.description}</span>
                </div>
              </label>
            ))}
          </div>
        </section>

        {/* アルゴリズムパラメータ */}
        <section className="algorithm-params">
          <h3>パラメータ調整</h3>

          {(() => {
            const selectedAlgoInfo = getSelectedAlgorithmInfo();
            if (!selectedAlgoInfo || !algorithmParams[selectedAlgorithm]) {
              return <div>選択されたアルゴリズムの設定がありません</div>;
            }

            return (
              <div className="param-group">
                <h4>{selectedAlgoInfo.name}パラメータ</h4>
                
                <div className="param-grid">
                  {selectedAlgoInfo.parameters_metadata.map((param) => {
                    const currentValue = algorithmParams[selectedAlgorithm][param.key];
                    
                    return (
                      <div key={param.key} className="param-item">
                        <label htmlFor={`param-${param.key}`} className="param-label">
                          <span className="param-name">{param.display_name_ja}</span>
                          {param.unit && <span className="param-unit">({param.unit})</span>}
                        </label>
                        <div className="param-control">
                          {renderParameterInput(param, currentValue)}
                          <div className="param-info">
                            <span className="param-default">
                              デフォルト: {selectedAlgoInfo.default_params[param.key]}
                              {param.unit && ` ${param.unit}`}
                            </span>
                            {(param.min !== undefined || param.max !== undefined) && (
                              <span className="param-range">
                                範囲: {param.min ?? '∞'} - {param.max ?? '∞'}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })()}
        </section>

        {/* 分析期間設定 */}
        <section className="analysis-settings">
          <h3>分析期間設定</h3>
          
          <div className="param-item">
            <label>分析期間 (営業日数)</label>
            {(() => {
              // 現在のパラメータに基づく必要日数をバックエンドから取得（SSOT原則）
              const currentParams = algorithmParams[selectedAlgorithm] || {};

              // バックエンドから計算された値を使用、計算中またはエラー時は30をフォールバック
              const minimumDays = calculatedRequiredDays !== null ? calculatedRequiredDays : 30;
              const recommendedDays = Math.max(minimumDays + 5, 60); // 余裕を持たせる
              
              return (
                <>
                  <input
                    type="number"
                    value={selectedAlgorithm === 'linear_uptrend' ? minimumDays : analysisParams.analysis_period_days}
                    min={minimumDays}
                    max={365}
                    disabled={selectedAlgorithm === 'linear_uptrend'}
                    onChange={(e) => {
                      const inputValue = e.target.value;

                      // 空文字列の場合はそのまま許可（入力中の状態）
                      if (inputValue === '') {
                        setAnalysisParams({
                          ...analysisParams,
                          analysis_period_days: '' as any
                        });
                        return;
                      }

                      const value = parseInt(inputValue);
                      if (!isNaN(value)) {
                        setAnalysisParams({
                          ...analysisParams,
                          analysis_period_days: value
                        });
                      }
                    }}
                    onBlur={(e) => {
                      const inputValue = e.target.value;
                      const value = parseInt(inputValue);

                      // フォーカスが外れた時に最小値チェック
                      if (isNaN(value) || value < minimumDays) {
                        setAnalysisParams({
                          ...analysisParams,
                          analysis_period_days: minimumDays
                        });
                      }
                    }}
                    style={selectedAlgorithm === 'linear_uptrend' ? {
                      backgroundColor: '#f5f5f5',
                      color: '#666',
                      cursor: 'not-allowed'
                    } : {}}
                  />
                  <div className="param-info">
                    {selectedAlgorithm === 'linear_uptrend' ? (
                      <span className="param-description" style={{ color: '#666' }}>
                        このアルゴリズムでは上昇検出期間がそのまま必要な分析期間になります
                      </span>
                    ) : (
                      <span className="param-description">過去何営業日分のデータを分析対象とするか</span>
                    )}
                    {selectedAlgorithm === 'linear_uptrend' && (
                      <span className="param-formula" style={{
                        display: 'block',
                        fontSize: '1.0em',
                        color: '#2c3e50',
                        backgroundColor: '#e8f4f8',
                        padding: '0.8rem',
                        borderRadius: '4px',
                        marginTop: '0.5rem',
                        lineHeight: '1.8'
                      }}>
                        ℹ️ <strong>必要営業日数：</strong>
                        上昇検出期間({currentParams.uptrend_detection_days || 30}日)
                        = <strong>{minimumDays}営業日</strong>
                      </span>
                    )}
                    {(selectedAlgorithm === 'simple_trend_line' ||
                      selectedAlgorithm === 'simple_trend_ma') && (
                      <span className="param-formula" style={{
                        display: 'block',
                        fontSize: '1.0em',
                        color: '#2c3e50',
                        backgroundColor: '#ecf0f1',
                        padding: '0.8rem',
                        borderRadius: '4px',
                        marginTop: '0.5rem',
                        lineHeight: '1.8'
                      }}>
                        📐 <strong>必要営業日数の計算式：</strong><br/>
                        線形回帰窓幅({currentParams.slope_window || 15})
                        + 横ばい最小継続日数({currentParams.flat_min_len || 10})
                        + 上昇最小継続日数({currentParams.up_min_len || 10})
                        + 安定性確認期間({currentParams.confirm_window || 20})
                        = <strong>{minimumDays}営業日</strong>
                      </span>
                    )}
                    {selectedAlgorithm !== 'linear_uptrend' && (
                      <>
                        <span className="param-warning" style={{ color: '#ff6b6b', fontSize: '0.9em' }}>
                          ※ 現在のパラメータ設定では最低{minimumDays}営業日必要です（推奨: {recommendedDays}営業日以上）
                        </span>
                        {analysisParams.analysis_period_days < minimumDays && (
                          <span className="param-error" style={{ color: '#ff0000', fontWeight: 'bold' }}>
                            ⚠️ 分析期間が不足しています！{minimumDays}営業日以上に設定してください
                          </span>
                        )}
                      </>
                    )}
                  </div>
                </>
              );
            })()}
          </div>
        </section>

        {/* 保存・リセットボタン */}
        <div className="settings-actions">
          <button onClick={handleSave} className="save-button">
            設定を保存
          </button>
          <button onClick={handleReset} className="reset-button">
            デフォルトに戻す
          </button>
        </div>
      </div>
    </div>
  );
};

export default Settings;